import Eventable from '../eventable';
import { getLogger } from '../logging/logger';

const logger = getLogger('GameClock');

const EVENTS = Object.freeze({
	start: 'start',
	stop: 'stop',
	reset: 'reset',
	tick: 'tick',
	timeLow: 'time-low',
	timeExpired: 'time-expired',
});

const ATTRIBUTES = Object.freeze({
	tickInterval: 'tickInterval',
	timeLimit: 'timeLimit',
	warningTime: 'warningTime',
});

const DEFAULTS = {
	// Externally configurable values
	[ATTRIBUTES.tickInterval]: 200,
	[ATTRIBUTES.timeLimit]: Infinity,
	[ATTRIBUTES.warningTime]: 0,
	// Run time values
	intervalStartTime: null,
	timeElapsed: 0,
	interval: null,
	lowTimeTriggered: false,
};

export { EVENTS, ATTRIBUTES };
export default class GameClock extends Eventable {
	constructor(options = {}) {
		super();

		this.tickInterval = DEFAULTS.tickInterval;
		this.timeLimit = DEFAULTS.timeLimit;
		this.warningTime = DEFAULTS.warningTime;

		/** @type {NodeJS.Timer} */
		this.interval = undefined;

		Object.assign(this, DEFAULTS, options);
	}

	reset() {
		logger.log('[reset]');

		this.timeElapsed = 0;
		this.intervalStartTime = Date.now();
		this.lowTimeTriggered = false;

		this.trigger(EVENTS.reset);
		return this;
	}

	start() {
		logger.log('[start]');

		if (!this.isRunning()) {
			this.intervalStartTime = Date.now();
			this.interval = setInterval(this.tick.bind(this), this.tickInterval);
		}
		this.trigger(EVENTS.start);
		return this;
	}

	stop() {
		logger.log('[stop]');

		if (this.isRunning()) this.timeElapsed += Date.now() - this.intervalStartTime;
		clearInterval(this.interval);
		this.interval = null;
		this.intervalStartTime = null;

		this.trigger(EVENTS.stop);
		return this;
	}

	isRunning() {
		return Number.isInteger(this.interval);
	}

	getTimeLimit() {
		logger.log('[getTimeLimit]');
		return this.timeLimit;
	}

	getElapsedTime() {
		logger.debug('[getElapsedTime]');

		let delta = 0;
		if (this.intervalStartTime && Number.isInteger(this.intervalStartTime)) {
			delta = Date.now() - this.intervalStartTime;
		}
		return this.timeElapsed + delta;
	}

	getRemainingTime() {
		return this.timeLimit - this.getElapsedTime();
	}

	addTime(timeDelta) {
		logger.log('[addTime]', { timeDelta });

		if (typeof timeDelta === 'string') {
			timeDelta = parseInt(timeDelta);
		}
		if (Number.isInteger(timeDelta)) {
			this.timeElapsed += timeDelta;
		}
		return this;
	}

	tick() {
		logger.debug('[tick]');

		const currentTime = this.getElapsedTime();
		if (currentTime > this.timeLimit) {
			this.stop();
			this.trigger(EVENTS.timeExpired);
			return;
		}

		if (!this.lowTimeTriggered && currentTime >= this.timeLimit - this.warningTime) {
			this.trigger(EVENTS.timeLow);
			this.lowTimeTriggered = true;
		}

		this.trigger(EVENTS.tick, currentTime);
	}
}
